import { API_CONFIG, API_ENDPOINTS, getHeaders, getMultipartHeaders, apiCall } from './client';
import {
  EvalDataset,
  EvalDatasetDetails,
  EvalDatasetListResponse,
  EvalDatasetUpdatePayload,
  ExperimentStatus,
  ExperimentListResponse,
  ExperimentResults,
  ContentOptimizationSuggestResponse,
  ContentOptimizationApplyResponse,
} from '../../types';

// =============================================================================
// Evaluation Dataset API
// =============================================================================

export async function uploadEvalDataset(
  siteId: string,
  orgId: string,
  file: File,
  name: string,
  description?: string,
): Promise<EvalDataset> {
  const url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.EVAL_DATASETS(siteId)}`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('name', name);
  if (description) {
    formData.append('description', description);
  }

  return apiCall<EvalDataset>(url, {
    method: 'POST',
    headers: getMultipartHeaders(orgId),
    body: formData,
  });
}

export async function listEvalDatasets(
  siteId: string,
  orgId: string,
): Promise<EvalDatasetListResponse> {
  return apiCall<EvalDatasetListResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.EVAL_DATASETS(siteId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    },
  );
}

export async function getEvalDataset(
  siteId: string,
  orgId: string,
  datasetId: string,
): Promise<EvalDatasetDetails> {
  return apiCall<EvalDatasetDetails>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.EVAL_DATASET(siteId, datasetId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    },
  );
}

export async function updateEvalDataset(
  siteId: string,
  orgId: string,
  datasetId: string,
  payload: EvalDatasetUpdatePayload,
): Promise<EvalDatasetDetails> {
  return apiCall<EvalDatasetDetails>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.EVAL_DATASET(siteId, datasetId)}`,
    {
      method: 'PUT',
      headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
}

export async function deleteEvalDataset(
  siteId: string,
  orgId: string,
  datasetId: string,
): Promise<void> {
  return apiCall<void>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.EVAL_DATASET(siteId, datasetId)}`,
    {
      method: 'DELETE',
      headers: getHeaders(),
    },
  );
}

// ---------------------------------------------------------------------------
// Experiment APIs
// ---------------------------------------------------------------------------

export async function startExperiment(
  siteId: string,
  orgId: string,
  datasetId: string,
  experimentName?: string,
): Promise<ExperimentStatus> {
  const body: Record<string, string> = { dataset_id: datasetId };
  if (experimentName?.trim()) body.experiment_name = experimentName.trim();

  return apiCall<ExperimentStatus>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.EVAL_EXPERIMENTS(siteId)}`,
    {
      method: 'POST',
      headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export async function getExperimentStatus(
  siteId: string,
  orgId: string,
  experimentId: string,
): Promise<ExperimentStatus> {
  return apiCall<ExperimentStatus>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.EVAL_EXPERIMENT_STATUS(siteId, experimentId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    },
  );
}

export async function getExperimentResults(
  siteId: string,
  orgId: string,
  experimentId: string,
): Promise<ExperimentResults> {
  return apiCall<ExperimentResults>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.EVAL_EXPERIMENT_RESULTS(siteId, experimentId)}`,
    {
      method: 'GET',
      headers: getHeaders(),
    },
  );
}

export async function listExperiments(
  siteId: string,
  orgId: string,
  datasetId?: string,
): Promise<ExperimentListResponse> {
  let url = `${API_CONFIG.BASE_URL}${API_ENDPOINTS.EVAL_EXPERIMENTS(siteId)}`;
  if (datasetId) {
    url += `?datasetId=${encodeURIComponent(datasetId)}`;
  }

  return apiCall<ExperimentListResponse>(url, {
    method: 'GET',
    headers: getHeaders(),
  });
}

export interface ContentOptimizationSuggestPayload {
  question: string;
  actual_answer: string;
  expected_answer: string;
  actual_sources: string[];
  expected_sources: string[];
  precision?: number | null;
  recall?: number | null;
  precision_reason?: string;
  recall_reason?: string;
}

export async function suggestContentOptimization(
  siteId: string,
  orgId: string,
  payload: ContentOptimizationSuggestPayload,
): Promise<ContentOptimizationSuggestResponse> {
  return apiCall<ContentOptimizationSuggestResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.EVAL_CONTENT_OPTIMIZATION_SUGGEST(siteId)}`,
    {
      method: 'POST',
      headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
}

export interface ContentOptimizationApplyPayload {
  experiment_id: string;
  example_row_index: number;
  question: string;
  expected_answer: string;
  topics: Array<{ asset_guid: string; optimized_xml: string; approve: boolean }>;
}

export async function applyContentOptimization(
  siteId: string,
  orgId: string,
  payload: ContentOptimizationApplyPayload,
): Promise<ContentOptimizationApplyResponse> {
  return apiCall<ContentOptimizationApplyResponse>(
    `${API_CONFIG.BASE_URL}${API_ENDPOINTS.EVAL_CONTENT_OPTIMIZATION_APPLY(siteId)}`,
    {
      method: 'POST',
      headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
}
